/**
 * @typedef {Object} ChatOption
 * @property {string} value
 * @property {string} label
 */
/**
 * @typedef {Object} ChatStep
 * @property {string} id
 * @property {string} question
 * @property {ChatOption[]} options
 */
/**
 * @typedef {Object} ChatFlow
 * @property {string} id
 * @property {string} label
 * @property {ChatStep[]} steps
 * @property {(answers: Record<string, string>) => string} buildLocal
 */

export const chatFlows = [
  {
    id: "componentes",
    label: "Componentes básicos",
    steps: [
      {
        id: "componente",
        question: "¿Sobre qué componente querés aprender?",
        options: [
          { value: "cpu", label: "Procesador (CPU)" },
          { value: "gpu", label: "Placa de video (GPU)" },
          { value: "ram", label: "Memoria RAM" },
          { value: "ssd", label: "Disco SSD / HDD" },
          { value: "mother", label: "Placa madre" },
          { value: "fuente", label: "Fuente de poder" },
          { value: "gabinete", label: "Gabinete y flujo de aire" },
          { value: "refrigeracion", label: "Refrigeración (aire / líquida)" },
          { value: "monitor", label: "Monitor y pantalla" },
          { value: "perifericos", label: "Periféricos (teclado, mouse)" },
        ],
      },
      {
        id: "nivel",
        question: "¿A qué nivel querés la explicación?",
        options: [
          { value: "basico", label: "Básico" },
          { value: "intermedio", label: "Intermedio" },
          { value: "ejemplos", label: "Con ejemplos simples" },
          { value: "comparacion", label: "Con comparaciones rápidas" },
        ],
      },
      {
        id: "objetivo",
        question: "¿Qué te interesa aprender?",
        options: [
          { value: "funcion", label: "Para qué sirve" },
          { value: "rendimiento", label: "Cómo impacta en el rendimiento" },
          { value: "compatibilidad", label: "Compatibilidad" },
          { value: "cuidados", label: "Uso y cuidados" },
          { value: "seleccion", label: "Cómo elegir uno" },
        ],
      },
    ],
    buildLocal: (answers) => {
      const comp = answers.componente;
      const objetivo = answers.objetivo;
      const nivel = answers.nivel;
      let base = "Te explico de forma simple el componente elegido.";
      let ejemplo = "";
      let tips = "";

      if (comp === "cpu") {
        base = "La CPU es el cerebro del equipo: procesa las tareas.";
        ejemplo = "Ejemplo: más núcleos ayudan en multitarea y edición.";
        tips = "Tips: 1) Mirar núcleos/hilos. 2) Ver compatibilidad con placa madre. 3) Considerar consumo.";
      } else if (comp === "gpu") {
        base = "La GPU maneja gráficos, video y tareas paralelas.";
        ejemplo = "Ejemplo: una GPU mejor da más FPS y mejor edición.";
        tips = "Tips: 1) Ver VRAM. 2) Revisar fuente y tamaño del gabinete. 3) Controlar temperaturas.";
      } else if (comp === "ram") {
        base = "La RAM guarda datos temporales para que todo vaya fluido.";
        ejemplo = "Ejemplo: pasar de 8GB a 16GB mejora multitarea.";
        tips = "Tips: 1) Ver capacidad. 2) Revisar velocidad compatible. 3) Usar dual channel.";
      } else if (comp === "ssd") {
        base = "El almacenamiento define velocidad de carga y respuesta.";
        ejemplo = "Ejemplo: un SSD arranca en segundos frente a un HDD.";
        tips = "Tips: 1) Elegir SSD para sistema. 2) Dejar espacio libre. 3) Revisar compatibilidad SATA/NVMe.";
      } else if (comp === "mother") {
        base = "La placa madre conecta y define compatibilidad del equipo.";
        ejemplo = "Ejemplo: un chipset nuevo habilita CPUs y RAM más rápidas.";
        tips = "Tips: 1) Ver socket. 2) Revisar puertos necesarios. 3) Chequear soporte de RAM y almacenamiento.";
      } else if (comp === "fuente") {
        base = "La fuente alimenta todos los componentes.";
        ejemplo = "Ejemplo: una fuente justa puede causar apagones.";
        tips = "Tips: 1) Elegir potencia real. 2) Buscar certificación. 3) Pensar en futuras mejoras.";
      } else if (comp === "gabinete") {
        base = "El gabinete define espacio y flujo de aire.";
        ejemplo = "Ejemplo: buen flujo baja temperaturas y ruido.";
        tips = "Tips: 1) Ver tamaño. 2) Revisar ventiladores. 3) Ordenar cables.";
      } else if (comp === "refrigeracion") {
        base = "La refrigeración mantiene temperaturas estables.";
        ejemplo = "Ejemplo: un mejor cooler evita throttling.";
        tips = "Tips: 1) Aire es más simple. 2) Líquida requiere mantenimiento. 3) Usar pasta térmica adecuada.";
      } else if (comp === "monitor") {
        base = "El monitor define calidad de imagen y fluidez.";
        ejemplo = "Ejemplo: 144 Hz se siente más fluido que 60 Hz.";
        tips = "Tips: 1) Elegir resolución según GPU. 2) Mirar Hz. 3) Revisar tipo de panel.";
      } else if (comp === "perifericos") {
        base = "Los periféricos mejoran la experiencia de uso.";
        ejemplo = "Ejemplo: un mouse con buen sensor ayuda en juegos.";
        tips = "Tips: 1) Priorizar comodidad. 2) Elegir según uso. 3) Ver garantía.";
      }

      const extras = [];
      if (objetivo === "rendimiento") {
        extras.push("Impacta directamente en la fluidez y tiempos de respuesta.");
      }
      if (objetivo === "compatibilidad") {
        extras.push("Revisar compatibilidad evita errores al armar o actualizar.");
      }
      if (objetivo === "cuidados") {
        extras.push("Un buen mantenimiento alarga la vida útil.");
      }
      if (objetivo === "seleccion") {
        extras.push("La elección depende de uso, presupuesto y compatibilidad.");
      }

      let nivelExtra = "";
      if (nivel === "basico") {
        tips = "Tip: priorizá lo esencial y compatibilidad.";
      } else if (nivel === "intermedio") {
        nivelExtra = "Detalle: también importa la calidad de componentes y el equilibrio del sistema.";
      } else if (nivel === "ejemplos") {
        nivelExtra = "Ejemplo extra: mejorar este componente suele sentirse en el uso diario.";
      } else if (nivel === "comparacion") {
        nivelExtra = "Comparación rápida: opciones más potentes cuestan más pero rinden mejor.";
      }

      return `${base} ${extras.join(" ")} ${ejemplo} ${nivelExtra} ${tips} Si querés una recomendación exacta para tu equipo, contactá al técnico especializado.`.trim();
    },
  },
  {
    id: "comparativas",
    label: "Comparativas",
    steps: [
      {
        id: "comparar",
        question: "¿Qué querés comparar?",
        options: [
          { value: "cpu", label: "CPU (procesadores)" },
          { value: "gpu", label: "GPU (placas de video)" },
          { value: "ram", label: "RAM (capacidad / velocidad)" },
          { value: "ssd", label: "SSD vs HDD" },
          { value: "nvme", label: "SSD NVMe vs SATA" },
          { value: "ddr", label: "DDR4 vs DDR5" },
          { value: "monitores", label: "Monitores (60/144/240 Hz)" },
        ],
      },
      {
        id: "uso",
        question: "¿Para qué uso principal?",
        options: [
          { value: "ofimatica", label: "Ofimática / estudio" },
          { value: "juegos", label: "Juegos" },
          { value: "edicion", label: "Edición / diseño" },
          { value: "mixto", label: "Uso mixto" },
          { value: "streaming", label: "Streaming" },
        ],
      },
      {
        id: "prioridad",
        question: "¿Qué priorizás?",
        options: [
          { value: "rendimiento", label: "Rendimiento" },
          { value: "precio", label: "Precio" },
          { value: "consumo", label: "Bajo consumo" },
          { value: "compatibilidad", label: "Compatibilidad" },
          { value: "durabilidad", label: "Durabilidad" },
        ],
      },
      {
        id: "nivel",
        question: "¿A qué nivel querés la explicación?",
        options: [
          { value: "basico", label: "Básico" },
          { value: "intermedio", label: "Intermedio" },
          { value: "ejemplos", label: "Con ejemplos simples" },
          { value: "comparacion", label: "Con comparaciones rápidas" },
        ],
      },
    ],
    buildLocal: (answers) => {
      const comp = answers.comparar;
      const uso = answers.uso;
      const prioridad = answers.prioridad;
      const nivel = answers.nivel;
      let base = "En comparativas conviene mirar rendimiento real, consumo y compatibilidad.";
      let ejemplo = "Ejemplo: NVMe suele cargar más rápido que SATA.";
      let tips = "Tips: 1) Comparar benchmarks reales. 2) Mirar consumo. 3) Ver compatibilidad.";

      if (comp === "cpu") {
        ejemplo = "Ejemplo: más núcleos ayuda en edición, más frecuencia ayuda en juegos.";
      } else if (comp === "gpu") {
        ejemplo = "Ejemplo: más VRAM ayuda en resoluciones altas.";
      } else if (comp === "ram") {
        ejemplo = "Ejemplo: 16GB es un buen equilibrio para uso mixto.";
      } else if (comp === "ddr") {
        ejemplo = "Ejemplo: DDR5 ofrece más ancho de banda pero cuesta más.";
      } else if (comp === "monitores") {
        ejemplo = "Ejemplo: 144 Hz se nota más fluido que 60 Hz en juegos.";
      }

      if (uso === "ofimatica") {
        base = "Para ofimática, conviene priorizar estabilidad y precio.";
      } else if (uso === "juegos") {
        base = "Para juegos, conviene priorizar rendimiento y estabilidad.";
      } else if (uso === "edicion") {
        base = "Para edición, importan núcleos, RAM y almacenamiento rápido.";
      }

      if (prioridad === "precio") {
        tips = "Tips: 1) Buscar mejor precio/rendimiento. 2) Revisar usados con garantía. 3) No sobredimensionar.";
      } else if (prioridad === "consumo") {
        tips = "Tips: 1) Ver TDP. 2) Mejor eficiencia de fuente. 3) Controlar temperaturas.";
      } else if (prioridad === "durabilidad") {
        tips = "Tips: 1) Marcas confiables. 2) Buenas temperaturas. 3) Garantía extensa.";
      }

      let nivelExtra = "";
      if (nivel === "basico") {
        tips = "Tip: elegí lo más compatible con tu equipo.";
      } else if (nivel === "intermedio") {
        nivelExtra = "Detalle: mirá rendimiento por peso invertido.";
      } else if (nivel === "ejemplos") {
        nivelExtra = "Ejemplo extra: para 1080p, gama media suele rendir bien.";
      } else if (nivel === "comparacion") {
        nivelExtra = "Comparación rápida: más rendimiento suele implicar más consumo.";
      }

      return `${base} ${ejemplo} ${tips} Si querés una recomendación exacta para tu caso, contactá al técnico especializado.`.trim();
    },
  },
  {
    id: "sistemas",
    label: "Sistemas operativos",
    steps: [
      {
        id: "os",
        question: "¿Qué sistema operativo te interesa?",
        options: [
          { value: "windows", label: "Windows" },
          { value: "linux", label: "Linux" },
          { value: "macos", label: "macOS" },
          { value: "comparar", label: "Comparar entre ellos" },
        ],
      },
      {
        id: "uso",
        question: "¿Para qué uso principal?",
        options: [
          { value: "ofimatica", label: "Ofimática / estudio" },
          { value: "juegos", label: "Juegos" },
          { value: "edicion", label: "Edición / diseño" },
          { value: "programacion", label: "Programación" },
        ],
      },
      {
        id: "equipo",
        question: "¿Qué tipo de equipo tenés?",
        options: [
          { value: "desktop", label: "PC de escritorio" },
          { value: "notebook", label: "Notebook" },
        ],
      },
      {
        id: "nivel",
        question: "¿A qué nivel querés la explicación?",
        options: [
          { value: "basico", label: "Básico" },
          { value: "intermedio", label: "Intermedio" },
          { value: "ejemplos", label: "Con ejemplos simples" },
          { value: "comparacion", label: "Con comparaciones rápidas" },
        ],
      },
    ],
    buildLocal: (answers) => {
      const os = answers.os;
      const uso = answers.uso;
      const nivel = answers.nivel;
      let base = "Windows ofrece compatibilidad amplia; Linux es liviano y flexible; macOS optimiza hardware Apple.";
      let ejemplo = "Ejemplo: para juegos, Windows suele tener más soporte.";
      let tips = "Tips: 1) Revisar compatibilidad de apps. 2) Pensar en uso diario. 3) Ver requisitos del equipo.";

      if (os === "linux") {
        ejemplo = "Ejemplo: Linux es ideal para programación y servidores.";
      } else if (os === "macos") {
        ejemplo = "Ejemplo: macOS destaca en ecosistema Apple.";
      }

      if (uso === "juegos") {
        base = "Para juegos, Windows suele ser la opción más compatible.";
      } else if (uso === "programacion") {
        base = "Para programación, Linux o macOS suelen ser opciones sólidas.";
      } else if (uso === "edicion") {
        base = "Para edición, importa compatibilidad de software y drivers.";
      }

      let nivelExtra = "";
      if (nivel === "basico") {
        tips = "Tip: elegí el sistema con más compatibilidad para tu uso.";
      } else if (nivel === "intermedio") {
        nivelExtra = "Detalle: drivers y ecosistema influyen en estabilidad.";
      } else if (nivel === "ejemplos") {
        nivelExtra = "Ejemplo extra: Linux es ideal en equipos modestos.";
      } else if (nivel === "comparacion") {
        nivelExtra = "Comparación rápida: Windows = compatibilidad, Linux = liviano, macOS = ecosistema.";
      }

      return `${base} ${ejemplo} ${nivelExtra} ${tips} Para elegir con precisión, contactá al técnico especializado.`.trim();
    },
  },
  {
    id: "tecnologias",
    label: "Tecnologías y usos",
    steps: [
      {
        id: "tecnologia",
        question: "¿Qué tecnología querés entender?",
        options: [
          { value: "nvme", label: "SSD NVMe" },
          { value: "ddr5", label: "RAM DDR5" },
          { value: "raytracing", label: "Ray Tracing" },
          { value: "wifi6", label: "Wi-Fi 6" },
          { value: "usb-c", label: "USB-C / Thunderbolt" },
          { value: "pcie", label: "PCIe 3.0 / 4.0 / 5.0" },
          { value: "dlss", label: "DLSS / FSR" },
          { value: "hdr", label: "HDR en monitores" },
        ],
      },
      {
        id: "uso",
        question: "¿En qué uso te interesa más?",
        options: [
          { value: "juegos", label: "Juegos" },
          { value: "productividad", label: "Productividad" },
          { value: "creacion", label: "Creación de contenido" },
          { value: "general", label: "Uso general" },
          { value: "movilidad", label: "Movilidad / trabajo remoto" },
        ],
      },
      {
        id: "equipo",
        question: "¿Es equipo nuevo o actualización?",
        options: [
          { value: "nuevo", label: "Nuevo" },
          { value: "actualizar", label: "Actualizar equipo" },
        ],
      },
      {
        id: "nivel",
        question: "¿A qué nivel querés la explicación?",
        options: [
          { value: "basico", label: "Básico" },
          { value: "intermedio", label: "Intermedio" },
          { value: "ejemplos", label: "Con ejemplos simples" },
          { value: "comparacion", label: "Con comparaciones rápidas" },
        ],
      },
    ],
    buildLocal: (answers) => {
      const tech = answers.tecnologia;
      const nivel = answers.nivel;
      let base = "El impacto de cada tecnología depende del uso y la compatibilidad.";
      let ejemplo = "Ejemplo: Wi-Fi 6 mejora estabilidad si el router lo soporta.";
      let tips = "Tips: 1) Ver compatibilidad. 2) Evaluar beneficio real. 3) Considerar costo.";

      if (tech === "pcie") {
        ejemplo = "Ejemplo: PCIe 4.0 mejora velocidades en SSD y GPU.";
      } else if (tech === "dlss") {
        ejemplo = "Ejemplo: DLSS/FSR sube FPS con buena calidad visual.";
      } else if (tech === "hdr") {
        ejemplo = "Ejemplo: HDR mejora contraste si el panel lo soporta bien.";
      }

      let nivelExtra = "";
      if (nivel === "basico") {
        tips = "Tip: buscá compatibilidad antes de comprar.";
      } else if (nivel === "intermedio") {
        nivelExtra = "Detalle: el beneficio depende del hardware actual.";
      } else if (nivel === "ejemplos") {
        nivelExtra = "Ejemplo extra: NVMe se nota más al abrir programas.";
      } else if (nivel === "comparacion") {
        nivelExtra = "Comparación rápida: versiones nuevas suelen rendir más pero cuestan más.";
      }

      return `${base} ${ejemplo} ${nivelExtra} ${tips} Para una recomendación segura, contactá al técnico especializado.`.trim();
    },
  },
  {
    id: "recomendaciones",
    label: "Recomendaciones de compra",
    steps: [
      {
        id: "tipo",
        question: "¿Qué querés comprar o mejorar?",
        options: [
          { value: "pc", label: "PC completa" },
          { value: "gpu", label: "Placa de video" },
          { value: "cpu", label: "Procesador" },
          { value: "ram", label: "Memoria RAM" },
          { value: "ssd", label: "Disco SSD" },
          { value: "monitor", label: "Monitor" },
          { value: "perifericos", label: "Periféricos" },
          { value: "fuente", label: "Fuente de poder" },
        ],
      },
      {
        id: "uso",
        question: "¿Para qué uso principal?",
        options: [
          { value: "ofimatica", label: "Ofimática / estudio" },
          { value: "juegos", label: "Juegos" },
          { value: "edicion", label: "Edición / diseño" },
          { value: "mixto", label: "Uso mixto" },
          { value: "profesional", label: "Trabajo profesional" },
        ],
      },
      {
        id: "presupuesto",
        question: "¿Cómo es tu presupuesto?",
        options: [
          { value: "ajustado", label: "Ajustado" },
          { value: "medio", label: "Intermedio" },
          { value: "alto", label: "Alto" },
          { value: "no-se", label: "No lo sé" },
        ],
      },
      {
        id: "prioridad",
        question: "¿Qué priorizás en la compra?",
        options: [
          { value: "precio", label: "Mejor precio" },
          { value: "calidad", label: "Calidad / durabilidad" },
          { value: "equilibrio", label: "Equilibrio precio-rendimiento" },
          { value: "futuro", label: "Preparado para el futuro" },
        ],
      },
      {
        id: "nivel",
        question: "¿A qué nivel querés la explicación?",
        options: [
          { value: "basico", label: "Básico" },
          { value: "intermedio", label: "Intermedio" },
          { value: "ejemplos", label: "Con ejemplos simples" },
          { value: "comparacion", label: "Con comparaciones rápidas" },
        ],
      },
    ],
    buildLocal: (answers) => {
      const tipo = answers.tipo;
      const uso = answers.uso;
      const nivel = answers.nivel;
      let base = "Para recomendar compras conviene definir presupuesto, compatibilidad y uso real.";
      let ejemplo = "Ejemplo: para ofimática, un SSD y 8GB de RAM es una base sólida.";
      let tips = "Tips: 1) Priorizar uso. 2) Mirar compatibilidad. 3) Equilibrar precio y rendimiento.";

      if (tipo === "gpu" && uso === "juegos") {
        ejemplo = "Ejemplo: para 1080p, una GPU de gama media suele alcanzar.";
      } else if (tipo === "pc" && uso === "profesional") {
        ejemplo = "Ejemplo: para trabajo profesional, priorizá estabilidad y RAM.";
      }

      let nivelExtra = "";
      if (nivel === "basico") {
        tips = "Tip: no compres de más si no lo necesitás.";
      } else if (nivel === "intermedio") {
        nivelExtra = "Detalle: pensá en futuras mejoras.";
      } else if (nivel === "ejemplos") {
        nivelExtra = "Ejemplo extra: para estudio, un equipo equilibrado rinde bien.";
      } else if (nivel === "comparacion") {
        nivelExtra = "Comparación rápida: más rendimiento suele costar más.";
      }

      return `${base} ${ejemplo} ${nivelExtra} ${tips} Si querés una sugerencia precisa, contactá al técnico especializado.`.trim();
    },
  },
  {
    id: "mantenimiento",
    label: "Mantenimiento y cuidados",
    steps: [
      {
        id: "tema",
        question: "¿Qué querés aprender sobre mantenimiento?",
        options: [
          { value: "limpieza", label: "Limpieza y polvo" },
          { value: "temperaturas", label: "Temperaturas y refrigeración" },
          { value: "pasta", label: "Pasta térmica" },
          { value: "drivers", label: "Drivers y actualizaciones" },
          { value: "almacenamiento", label: "Salud de discos" },
        ],
      },
      {
        id: "frecuencia",
        question: "¿Con qué frecuencia lo hacés?",
        options: [
          { value: "nunca", label: "Nunca" },
          { value: "anual", label: "Una vez al año" },
          { value: "semestral", label: "Cada 6 meses" },
          { value: "trimestral", label: "Cada 3 meses" },
        ],
      },
      {
        id: "entorno",
        question: "¿En qué entorno está el equipo?",
        options: [
          { value: "polvo", label: "Con polvo / humo" },
          { value: "normal", label: "Normal" },
          { value: "limpio", label: "Muy limpio" },
        ],
      },
      {
        id: "nivel",
        question: "¿A qué nivel querés la explicación?",
        options: [
          { value: "basico", label: "Básico" },
          { value: "intermedio", label: "Intermedio" },
          { value: "ejemplos", label: "Con ejemplos simples" },
          { value: "comparacion", label: "Con comparaciones rápidas" },
        ],
      },
    ],
    buildLocal: (answers) => {
      const tema = answers.tema;
      const nivel = answers.nivel;
      let base = "El mantenimiento preventivo mejora rendimiento y vida útil.";
      let ejemplo = "Ejemplo: limpiar polvo cada 6 meses reduce temperaturas.";
      let tips = "Tips: 1) Limpieza regular. 2) Revisar temperaturas. 3) Actualizar drivers.";

      if (tema === "pasta") {
        ejemplo = "Ejemplo: cambiar pasta térmica puede bajar varios grados.";
      } else if (tema === "drivers") {
        ejemplo = "Ejemplo: drivers actualizados mejoran estabilidad.";
      } else if (tema === "almacenamiento") {
        ejemplo = "Ejemplo: revisar SMART evita sorpresas en discos.";
      }

      let nivelExtra = "";
      if (nivel === "basico") {
        tips = "Tip: una limpieza básica ya ayuda mucho.";
      } else if (nivel === "intermedio") {
        nivelExtra = "Detalle: monitorear temperaturas evita desgaste.";
      } else if (nivel === "ejemplos") {
        nivelExtra = "Ejemplo extra: polvo acumulado aumenta ruido y calor.";
      } else if (nivel === "comparacion") {
        nivelExtra = "Comparación rápida: equipos limpios duran más.";
      }

      return `${base} ${ejemplo} ${nivelExtra} ${tips} Si querés una revisión completa, contactá al técnico especializado.`.trim();
    },
  },
];

